import React, { useState, useEffect, useRef } from 'react';

import { PATREON_LABEL, PATREON_SLIDES as SLIDES } from '../../data/monthly';

const AUTO_INTERVAL = 10000;
const TRANSITION_MS = 350;

export function HeroBanner() {
  const [current, setCurrent] = useState(0);
  const [sliding, setSliding] = useState(false);
  const timerRef = useRef(null);
  const touchStartX = useRef(null);
  const slidingRef = useRef(false);

  const goTo = (index) => {
    if (slidingRef.current) return;
    slidingRef.current = true;
    setSliding(true);
    setCurrent(index);
    setTimeout(() => {
      setSliding(false);
      slidingRef.current = false;
    }, TRANSITION_MS);
  };

  const resetTimer = () => {
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      if (!slidingRef.current) {
        slidingRef.current = true;
        setSliding(true);
        setCurrent(prev => (prev + 1) % SLIDES.length);
        setTimeout(() => {
          setSliding(false);
          slidingRef.current = false;
        }, TRANSITION_MS);
      }
    }, AUTO_INTERVAL);
  };

  useEffect(() => {
    resetTimer();
    return () => clearInterval(timerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDotClick = (index) => {
    if (index === current) return;
    goTo(index);
    resetTimer();
  };

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const delta = touchStartX.current - e.changedTouches[0].clientX;
    touchStartX.current = null;
    if (Math.abs(delta) < 40) return;
    const next = delta > 0
      ? (current + 1) % SLIDES.length
      : (current - 1 + SLIDES.length) % SLIDES.length;
    goTo(next);
    resetTimer();
  };

  return (
    <div
      style={styles.wrapper}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {/* Blurred background — changes instantly */}
      <div
        style={{
          ...styles.bgBlur,
          backgroundImage: `url(${SLIDES[current].src})`,
        }}
      />

      {/* Sliding track */}
      <div
        style={{
          ...styles.track,
          transform: `translateX(-${current * (100 / SLIDES.length)}%)`,
          transition: sliding ? `transform ${TRANSITION_MS}ms cubic-bezier(0.25, 0.46, 0.45, 0.94)` : 'none',
        }}
      >
        {SLIDES.map((slide, i) => (
          <div key={i} style={styles.slide}>
            <div style={styles.content}>
              <div style={styles.imgWrap}>
                <img
                  src={slide.src}
                  alt={`Banner ${i + 1}`}
                  style={styles.img}
                  loading={i === 0 ? 'eager' : 'lazy'}
                  decoding={i === 0 ? 'sync' : 'async'}
                />
              </div>
              <div style={styles.textWrap}>
                <span className="fp-chip" style={styles.label}>{PATREON_LABEL}</span>
                <p style={styles.name}>{slide.name}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Dots */}
      <div style={styles.dots}>
        {SLIDES.map((_, i) => (
          <button
            key={i}
            onClick={() => handleDotClick(i)}
            style={{ ...styles.dot, ...(i === current ? styles.dotActive : {}) }}
            aria-label={`Slide ${i + 1}`}
          />
        ))}
      </div>
    </div>
  );
}

const styles = {
  wrapper: {
    position: 'relative',
    height: '210px',
    margin: '26px 14px 0',
    overflow: 'hidden',
    backgroundColor: '#000',
    border: '3px solid #000',
    borderRadius: '20px',
    boxShadow: '0 6px 0 #000, 0 0 0 2px rgba(34, 224, 255, 0.9) inset, 0 12px 24px rgba(0, 0, 0, 0.45)',
  },
  bgBlur: {
    position: 'absolute',
    inset: '-20px',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    filter: 'blur(18px) brightness(0.4)',
    transition: 'background-image 0s',
  },
  track: {
    position: 'relative',
    display: 'flex',
    width: `${SLIDES.length * 100}%`,
    height: '210px',
    willChange: 'transform',
    backfaceVisibility: 'hidden',
    WebkitBackfaceVisibility: 'hidden',
  },
  slide: {
    width: `${100 / SLIDES.length}%`,
    height: '100%',
    flexShrink: 0,
  },
  content: {
    position: 'relative',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
  },
  imgWrap: {
    width: '52%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '8px',
    flexShrink: 0,
  },
  img: {
    maxHeight: '100%',
    maxWidth: '100%',
    objectFit: 'contain',
    borderRadius: '10px',
    border: '2.5px solid #000',
  },
  textWrap: {
    flex: 1,
    minWidth: 0,
    padding: '12px 14px 12px 6px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'center',
    gap: '10px',
  },
  label: {
    fontSize: '9.5px',
    padding: '4px 8px',
    letterSpacing: '0.2px',
    whiteSpace: 'nowrap',
  },
  name: {
    color: '#fff',
    fontSize: '21px',
    fontWeight: 900,
    fontStyle: 'italic',
    textShadow: 'var(--fp-outline)',
    margin: 0,
    lineHeight: '1.1',
  },
  dots: {
    position: 'absolute',
    bottom: '10px',
    left: '50%',
    transform: 'translateX(-50%)',
    display: 'flex',
    gap: '6px',
    zIndex: 2,
  },
  dot: {
    width: '7px',
    height: '7px',
    borderRadius: '50%',
    border: 'none',
    background: 'rgba(255,255,255,0.35)',
    cursor: 'pointer',
    padding: 0,
    transition: 'background 0.2s, transform 0.2s',
  },
  dotActive: {
    background: '#ffd400',
    transform: 'scale(1.5)',
  },
};
